import { computed, reactive, ref, watch } from "vue";
import { EXPORT_FORMAT, type ExportFormatType } from "hoxton-shared/enums/exports";
import {
    HTML_VIDEO_ENCODING_FORMAT,
    HTML_VIDEO_PRESET,
    type HtmlVideoEncodingFormatType,
    type HtmlVideoPresetType
} from "hoxton-shared/enums/htmlVideoExport";
import { validateCustomFfmpegParams } from "hoxton-shared/utils/validateCustomFfmpegParams";

import { DEFAULT_HTML_VIDEO_FPS } from "@/components/Download/consts";

function createCheckboxWithInput<T>(defaultValue: T) {
    const enabled = ref(!!defaultValue);
    const value = ref(defaultValue);

    watch(value, newValue => {
        if (newValue) {
            enabled.value = true;
        } else {
            enabled.value = false;
        }
    });

    return reactive({
        enabled,
        value
    });
}

const duration = createCheckboxWithInput<string | undefined>(undefined);
const fps = ref(String(DEFAULT_HTML_VIDEO_FPS));
const captureAudio = ref(true);
const optimisedVideoFrames = ref(false);
const videoPreset = ref<HtmlVideoPresetType>(HTML_VIDEO_PRESET.DEFAULT);
const encodingFormat = ref<HtmlVideoEncodingFormatType>(HTML_VIDEO_ENCODING_FORMAT.MP4);
const customFfmpegParams = ref("");

watch(videoPreset, (_newPreset, oldPreset) => {
    if (oldPreset === HTML_VIDEO_PRESET.CUSTOM) {
        customFfmpegParams.value = "";
    }
});

export function useDownloadHTMLVideo() {
    const customFfmpegParamsError = computed(() => {
        if (videoPreset.value !== HTML_VIDEO_PRESET.CUSTOM || !customFfmpegParams.value.trim()) {
            return "";
        }

        const result = validateCustomFfmpegParams(customFfmpegParams.value);
        return result.valid ? "" : (result.error ?? "Invalid custom ffmpeg params");
    });

    const isButtonDisabled = computed<Partial<Record<ExportFormatType, boolean>>>(() => ({
        [EXPORT_FORMAT.VIDEO]:
            videoPreset.value === HTML_VIDEO_PRESET.CUSTOM &&
            (!customFfmpegParams.value.trim() || !!customFfmpegParamsError.value)
    }));

    function resetDownloadOptions() {
        duration.enabled = false;
        duration.value = undefined;
        fps.value = String(DEFAULT_HTML_VIDEO_FPS);
        captureAudio.value = true;
        optimisedVideoFrames.value = false;
        videoPreset.value = HTML_VIDEO_PRESET.DEFAULT;
        encodingFormat.value = HTML_VIDEO_ENCODING_FORMAT.MP4;
        customFfmpegParams.value = "";
    }

    function computeQueryParams(exportFormat: ExportFormatType) {
        if (exportFormat !== EXPORT_FORMAT.VIDEO) {
            return {};
        }

        const params: {
            videoPreset: HtmlVideoPresetType;
            encodingFormat: HtmlVideoEncodingFormatType;
            captureAudio: boolean;
            droppedFramesOptimisation: boolean;
            fps: number;
            customFfmpegParams?: string;
            duration?: number;
        } = {
            videoPreset: videoPreset.value,
            encodingFormat: encodingFormat.value,
            captureAudio: captureAudio.value,
            droppedFramesOptimisation: optimisedVideoFrames.value,
            fps: Number(fps.value)
        };

        if (videoPreset.value === HTML_VIDEO_PRESET.CUSTOM) {
            params.customFfmpegParams = customFfmpegParams.value.trim();
        }

        if (duration.enabled && duration.value) {
            params.duration = Number(duration.value);
        }

        return params;
    }

    return {
        duration,
        fps,
        captureAudio,
        optimisedVideoFrames,
        videoPreset,
        encodingFormat,
        customFfmpegParams,
        customFfmpegParamsError,
        isButtonDisabled,

        // methods
        resetDownloadOptions,
        computeQueryParams
    };
}
